/**
 * js/client-intake-card.js — the "Client Forms" dashboard card.
 *
 * What is still out with a client, and what is back waiting on the agent, on
 * the one screen they land on between clients. Sending a form now ends on the
 * dashboard (js/client-intake-link.js), so this card is where that form goes
 * to be remembered; without it a sent form was visible only from inside the
 * quote it was sent from, which is the last place an agent looks once they
 * have moved on to the next client.
 *
 * EAGER, and its own module, for the reason js/client-intake-notify.js already
 * documents: js/dashboard-widgets.js is at its 1,800-line ceiling and is not
 * edited for this. So this file paints `#widgetClientForms` ITSELF and
 * schedules its own refresh — nothing in refreshAll() asks it for anything. A
 * MutationObserver on `#dashboardView`'s style attribute is what stands in for
 * that missing hook: goHome() and showDashboard() both un-hide that element, so
 * watching it catches every way the dashboard comes into view, including the
 * navigation the send flow now performs.
 *
 * ONE source of truth: the `list` action. The counts in the pills and the rows
 * beneath them are derived from the SAME response, so the card cannot tell the
 * agent "2 waiting" over a list showing three (the computePolicyStats lesson).
 * It deliberately does NOT read ClientIntakeNotify's `?view=stats` cache for
 * the numbers: that scan has its own cap and its own filters, and mixing the
 * two is exactly how the compliance widget and the compliance page came to
 * disagree. The badge and this card answer different questions from different
 * queries, and each stays internally consistent.
 *
 * Never a measured zero. `_state` is 'unknown' until a fetch has actually
 * succeeded, and unknown renders `—` with "Not loaded yet" — a card that says
 * "0 out" when it has never been able to ask is worse than one that says
 * nothing (CLAUDE.md's A-456 rule). For the same reason a FAILED poll keeps
 * the rows already on screen rather than dropping to empty: an outage must not
 * read as "nothing outstanding".
 *
 * Client names ride this card, so it reads the agent's own scope (the API
 * already limits `list` to the sender's links unless they are an agency admin)
 * and every value is escaped — `clientName` is typed by the agent and
 * `createdByEmail` comes from another member's account record.
 */
window.ClientIntakeCard = (function () {
    'use strict';

    const ENDPOINT = '/api/intake-links';
    const CARD_ID = 'widgetClientForms';
    const DASHBOARD_ID = 'dashboardView';

    // Matches ClientIntakeNotify: an awareness surface, not an alerting one.
    const TTL_MS = 180000;
    const MAX_CONSECUTIVE_FAILURES = 3;
    const FIRST_KICK_MS = 2500;
    const MAX_ROWS = 5;

    // The API's own vocabulary. `submitted` is the one status that is waiting on
    // the AGENT; the rest are waiting on the client.
    const OPEN_STATUSES = Object.freeze(['created', 'sent', 'opened', 'locked']);
    const KIND_TAG = Object.freeze({ personal: 'PL', commercial: 'COMM' });
    const KIND_TOOL = Object.freeze({ personal: 'quoting', commercial: 'commercial' });

    let _rows = [];
    let _counts = { waiting: 0, out: 0, expiring: 0, purgeSoon: 0 };
    let _state = 'unknown';        // 'unknown' until a fetch has succeeded
    let _ts = 0;
    let _inflight = null;
    let _failures = 0;
    let _timer = null;
    let _kick = null;
    let _observer = null;
    let _wired = false;

    /**
     * A fallback that actually escapes. A `(s) => s` or a bare `String(s)`
     * fallback is the shape CLAUDE.md calls out: in any context where
     * window.Utils has not loaded, the "escaped" value reaches innerHTML raw.
     */
    function esc(s) {
        if (window.Utils && typeof window.Utils.escapeHTML === 'function') return window.Utils.escapeHTML(s);
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
            { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
        ));
    }

    function escA(s) {
        if (window.Utils && typeof window.Utils.escapeAttr === 'function') return window.Utils.escapeAttr(s);
        return esc(s);
    }

    function iconHtml(name, size) {
        const src = window.DashboardIcons || window.DashboardWidgets;
        try {
            if (src && typeof src.icon === 'function') return src.icon(name, size || 16);
        } catch (_) { /* fall through to no glyph */ }
        return '';
    }

    /**
     * Whole AGENCY calendar days from `iso` until today; negative means still
     * in the future. Days on the office's calendar (Pacific), not 24-hour
     * buckets from the instant: a link that works through Sep 11 reads
     * "expires in 7d" all of Sep 4, which is what "works until Sep 11" in the
     * client's email means, where the bucket count said 8 until mid-afternoon.
     * The bucket arithmetic stays as the fallback for a page with no Utils.
     */
    function daysSince(iso) {
        const t = Date.parse(iso || '');
        if (!Number.isFinite(t)) return null;
        if (window.Utils && typeof Utils.agencyDayDiff === 'function') {
            const d = Utils.agencyDayDiff(t, Date.now());
            if (Number.isFinite(d)) return d;
        }
        return Math.floor((Date.now() - t) / 86400000);
    }

    function agoText(iso) {
        const d = daysSince(iso);
        if (d == null) return '';
        if (d <= 0) return 'today';
        if (d === 1) return 'yesterday';
        return `${d}d ago`;
    }

    function untilText(iso) {
        const d = daysSince(iso);
        if (d == null) return '';
        const left = -d;
        if (left <= 0) return 'expires today';
        if (left === 1) return 'expires tomorrow';
        return `expires in ${left}d`;
    }

    // ── the one derivation ─────────────────────────────────────────────

    /**
     * Counts + display rows from ONE `list` payload.
     *
     * Ordering is the card's whole argument for existing: what is waiting on
     * the agent leads, oldest first, because a submission ages toward the
     * 30-day purge and the oldest is the one closest to being lost. Links still
     * with the client follow, soonest to expire first, because that is the one
     * worth a nudge. Pure, so a test can pin the order without a DOM.
     */
    function summarize(links) {
        const rows = Array.isArray(links) ? links.filter((l) => l && typeof l === 'object') : [];
        const submitted = rows.filter((l) => l.status === 'submitted');
        const open = rows.filter((l) => OPEN_STATUSES.includes(l.status));
        // Applied, answers still on the server. Kept on the card for the
        // agency's "keep applied answers" days so whoever did NOT apply it (an
        // admin applied a colleague's form, or the other way round) can still
        // find the client and use the answers on their own quote.
        const applied = rows.filter((l) => l.status === 'applied' && l.answersHeld === true);
        const counts = {
            waiting: submitted.length,
            out: open.length,
            expiring: open.filter((l) => l.expiresSoon).length,
            purgeSoon: submitted.filter((l) => l.unappliedWarn).length,
            applied: applied.length,
        };
        const key = (iso) => {
            const t = Date.parse(iso || '');
            return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
        };
        const waiting = submitted
            .slice()
            .sort((a, b) => key(a.submittedAt) - key(b.submittedAt))
            .map((l) => ({ link: l, lane: 'waiting' }));
        const outstanding = open
            .slice()
            .sort((a, b) => key(a.expiresAt) - key(b.expiresAt))
            .map((l) => ({ link: l, lane: 'out' }));
        // Newest apply first: the one somebody just finished with is the one
        // the next person is looking for.
        const done = applied
            .slice()
            .sort((a, b) => key(b.appliedAt) - key(a.appliedAt))
            .map((l) => ({ link: l, lane: 'applied' }));
        return { counts, rows: waiting.concat(done, outstanding) };
    }

    /** "Applied today by AJK · kept until Oct 30". Shared with the Submissions panel. */
    function appliedText(l) {
        if (!l || !l.appliedAt) return '';
        let s = `Applied ${agoText(l.appliedAt)}`;
        const who = l.appliedByMe ? 'you' : (l.appliedByInitials || '');
        if (who) s += ` by ${who}`;
        if (l.answersKeptUntil) {
            const U = window.Utils;
            const day = U && typeof U.formatAgencyDate === 'function' ? U.formatAgencyDate(l.answersKeptUntil) : '';
            if (day) s += ` · kept until ${day.replace(/, \d{4}$/, '')}`;
        }
        return s;
    }

    /**
     * The card's tint. `neutral` is the not-loaded accent, never the green
     * all-clear — the A-456 distinction, applied here because "no forms out"
     * and "we could not ask" look identical otherwise.
     */
    function severity(counts, state) {
        if (state !== 'loaded') return 'severity-neutral';
        if (counts.purgeSoon > 0) return 'severity-critical';
        if (counts.waiting > 0) return 'severity-warning';
        return 'severity-ok';
    }

    // ── fetch ──────────────────────────────────────────────────────────

    function signedIn() {
        return typeof Auth !== 'undefined' && Auth.isSignedIn && typeof Auth.apiFetch === 'function';
    }

    // An admin hid both intakes for this agent (App.isToolListed, 0044): the
    // card is hidden with them (data-requires-tool in index.html), so it costs
    // no request. Fails open when the predicate is missing.
    function intakesListed() {
        const A = window.App;
        if (!A || typeof A.isToolListed !== 'function') return true;
        return A.isToolListed('quoting') || A.isToolListed('commercial');
    }

    function canPoll() {
        if (_failures >= MAX_CONSECUTIVE_FAILURES) return false;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
        if (!intakesListed()) return false;
        return signedIn();
    }

    /**
     * One in-flight `list` at a time. Both kinds in one request — the endpoint
     * returns every kind when `kind` is omitted, so the card costs one call,
     * not one per intake.
     */
    function load(force) {
        if (_inflight) return _inflight;
        const fresh = _ts && (Date.now() - _ts) < TTL_MS;
        if (fresh && !force) return Promise.resolve(_rows);
        if (force) _failures = 0;
        if (!canPoll()) return Promise.resolve(_rows);
        _inflight = Auth.apiFetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Closed rows too, so applied forms whose answers are still held
            // stay on the card; summarize() drops every other closed row.
            body: JSON.stringify({ action: 'list', includeClosed: true }),
        })
            .then((res) => {
                if (!res.ok) throw new Error(`client form list failed (${res.status})`);
                return res.json();
            })
            .then((json) => {
                if (!json || json.ok === false) throw new Error((json && json.error) || 'client form list refused');
                const summary = summarize(json.links);
                _rows = summary.rows;
                _counts = summary.counts;
                _state = 'loaded';
                _failures = 0;
                _ts = Date.now();
                render();
            })
            // Hold what is on screen. Dropping to an empty list on a failed poll
            // would tell the agent every form came back clean.
            .catch((err) => {
                _failures += 1;
                _ts = Date.now();
                if (_failures === MAX_CONSECUTIVE_FAILURES) {
                    console.warn(
                        `[ClientIntakeCard] Standing down after ${_failures} failures; the card keeps its last answer. Last error: ${err && err.message ? err.message : 'unknown'}`,
                    );
                }
                render();
            })
            .finally(() => { _inflight = null; });
        return _inflight;
    }

    // ── render ─────────────────────────────────────────────────────────

    const PROGRESS_STATUSES = Object.freeze(['sent', 'opened']);

    // The viewer's one-line "where the client is", read at call time.
    // "About 18 min on the form", from the viewer, read at call time.
    function timeLineFor(l) {
        const V = window.ClientIntakeView;
        if (!V || typeof V.timeLine !== 'function') return '';
        try { return V.timeLine(l) || ''; } catch (_) { return ''; }
    }

    function progressLineFor(l) {
        const V = window.ClientIntakeView;
        if (!V || typeof V.progressLine !== 'function') return '';
        try { return V.progressLine(l) || ''; } catch (_) { return ''; }
    }

    function senderOf(l) {
        const V = window.ClientIntakeView;
        if (V && typeof V.senderLabel === 'function') {
            try { return V.senderLabel(l) || l.createdByEmail || ''; } catch (_) { /* fall through */ }
        }
        return l.createdByInitials || l.createdByEmail || '';
    }

    function rowHtml(entry) {
        const l = entry.link;
        const kind = l.kind === 'commercial' ? 'commercial' : 'personal';
        const name = esc(l.clientName || l.recipientMasked || 'Client');
        const chips = [];
        let meta;
        if (entry.lane === 'applied') {
            const reuse = l.id ? ` data-cif-reuse="${escA(l.id)}" data-cif-reuse-kind="${escA(kind)}"` : '';
            if (l.mine === false) chips.push(`<span class="cif-chip">sent by ${esc(senderOf(l))}</span>`);
            return `<button type="button" class="cif-row"${reuse} title="Read the client's answers or use them on a quote">
            <span class="cif-dot cif-dot-applied"></span>
            <span class="cif-row-body">
                <span class="cif-row-title">${name}</span>
                <span class="cif-row-meta">${esc(appliedText(l))}</span>
            </span>
            <span class="cif-row-tail">
                <span class="cif-tag">${esc(KIND_TAG[kind])}</span>
                ${chips.join('')}
            </span>
        </button>`;
        }
        if (entry.lane === 'waiting') {
            meta = `Submitted ${agoText(l.submittedAt)}`;
            if (Number(l.answerCount) > 0) meta += ` · ${Number(l.answerCount)} answer${Number(l.answerCount) === 1 ? '' : 's'}`;
            const spent = timeLineFor(l);
            if (spent) meta += ` · ${spent.charAt(0).toLowerCase()}${spent.slice(1)}`;
            if (l.unappliedWarn) chips.push('<span class="cif-chip cif-chip-danger" title="Answers are purged 30 days after submission">purges soon</span>');
        } else {
            meta = l.sentAt ? `Sent ${agoText(l.sentAt)}` : 'Not sent yet';
            const until = untilText(l.expiresAt);
            if (until) meta += ` · ${until}`;
            if (l.expiresSoon) chips.push('<span class="cif-chip cif-chip-warn" title="The link expires today or tomorrow; resend to give the client more time">expiring</span>');
            const line = progressLineFor(l);
            if (line) meta += ` · ${line}`;
        }
        // A form still with the client opens straight onto its progress view,
        // which is what "where are they?" wants; everything else opens the
        // Submissions panel as before.
        const watch = entry.lane === 'out' && PROGRESS_STATUSES.includes(l.status) && l.id;
        if (watch) {
            return `<button type="button" class="cif-row" data-cif-view="${escA(l.id)}" title="See what the client has filled in so far">
            <span class="cif-dot cif-dot-${entry.lane}"></span>
            <span class="cif-row-body">
                <span class="cif-row-title">${name}</span>
                <span class="cif-row-meta">${esc(meta)}</span>
            </span>
            <span class="cif-row-tail">
                <span class="cif-tag">${esc(KIND_TAG[kind])}</span>
                ${chips.join('')}
            </span>
        </button>`;
        }
        // `mine === false` only ever appears for an agency admin looking at a
        // colleague's link, and whose form it is decides who chases it.
        if (l.mine === false && l.createdByEmail) chips.push(`<span class="cif-chip">sent by ${esc(senderOf(l))}</span>`);
        return `<button type="button" class="cif-row" data-cif-kind="${escA(kind)}" title="Open ${kind === 'commercial' ? 'Commercial' : 'Personal'} Intake submissions">
            <span class="cif-dot cif-dot-${entry.lane}"></span>
            <span class="cif-row-body">
                <span class="cif-row-title">${name}</span>
                <span class="cif-row-meta">${esc(meta)}</span>
            </span>
            <span class="cif-row-tail">
                <span class="cif-tag">${esc(KIND_TAG[kind])}</span>
                ${chips.join('')}
            </span>
        </button>`;
    }

    function render() {
        if (typeof document === 'undefined') return;
        const card = document.getElementById(CARD_ID);
        if (!card) return;

        const title = `<div class="widget-title">${iconHtml('mail', 16)} Client Forms</div>`;

        if (!signedIn()) {
            // No Submissions action here: it could only navigate to a tool that
            // shows the same refusal.
            card.className = card.className.replace(/severity-\w+/g, '').trim();
            card.classList.add('severity-neutral');
            card.innerHTML = `<div class="widget-header">${title}</div>`
                + '<div class="widget-empty"><p>Sign in to view client forms</p></div>';
            return;
        }

        const head = `<div class="widget-header">${title}
            <button type="button" class="widget-action" data-cif-act="open">Submissions →</button>
        </div>`;

        card.className = card.className.replace(/severity-\w+/g, '').trim();
        card.classList.add(severity(_counts, _state));

        const known = _state === 'loaded';
        const n = (v) => (known ? v : '—');
        const pills = `<div class="cif-stats" data-state="${known ? 'loaded' : 'unknown'}">
            <div class="cif-stat ${known && _counts.waiting > 0 ? 'stat-warning' : ''}">
                <span class="stat-count">${n(_counts.waiting)}</span>
                <span class="stat-label">Waiting</span>
            </div>
            <div class="cif-stat">
                <span class="stat-count">${n(_counts.out)}</span>
                <span class="stat-label">Out</span>
            </div>
            <div class="cif-stat ${known && _counts.expiring > 0 ? 'stat-warning' : ''}">
                <span class="stat-count">${n(_counts.expiring)}</span>
                <span class="stat-label">Expiring</span>
            </div>
        </div>`;

        let body;
        if (!known) {
            // Three different blanks, and they must not share a sentence.
            // "Checking…" while the last attempt actually FAILED is the lie
            // that matters here: the next automatic try is a whole TTL away,
            // so an agent reading it waits for an answer that is not coming.
            let note;
            if (_failures >= MAX_CONSECUTIVE_FAILURES) note = 'Could not reach the server. Open a quote to retry.';
            else if (_failures > 0) note = 'Could not reach the server. Trying again shortly.';
            else note = 'Checking for client forms…';
            body = `<p class="cif-note">${esc(note)}</p>`;
        } else if (!_rows.length) {
            body = '<p class="cif-note cif-empty">No client forms outstanding. Send one from a quote’s Review &amp; Export.</p>';
        } else {
            const shown = _rows.slice(0, MAX_ROWS);
            const more = _rows.length - shown.length;
            const moreHtml = more > 0
                ? `<button type="button" class="cif-more" data-cif-act="open">+${more} more</button>`
                : '';
            const stale = _failures > 0
                ? '<p class="cif-note cif-stale">Last refresh failed. Showing the previous answer.</p>'
                : '';
            body = `<div class="cif-list">${shown.map(rowHtml).join('')}</div>${moreHtml}${stale}`;
        }
        card.innerHTML = `${head}${pills}${body}`;
    }

    // ── interaction ────────────────────────────────────────────────────

    /**
     * Load js/client-intake-link.js WITHOUT navigating.
     *
     * The `quoting` entry from toolConfig, through the same seam
     * js/app-export-picker.js already uses for the composer. That module serves
     * BOTH kinds through its adapter table and rides both `scripts:` arrays, and
     * _ensurePluginScripts dedupes by src, so a later navigate to Commercial
     * reuses this tag rather than refetching. A synthetic {key, scripts} entry
     * would be lighter and would rot the day this module gains a dependency;
     * the list stays where p0-shell-scoping.test.js already pins its order.
     */
    async function ensureLinkModule() {
        if (window.ClientIntakeLink) return window.ClientIntakeLink;
        const A = window.App;
        if (!A || typeof A._ensurePluginScripts !== 'function') return null;
        const list = Array.isArray(A.toolConfig) ? A.toolConfig : [];
        const entry = list.find((t) => t && t.key === KIND_TOOL.personal);
        if (!entry) return null;
        try { await A._ensurePluginScripts(entry); } catch (_) { return null; }
        return window.ClientIntakeLink || null;
    }

    /**
     * Open the Submissions panel for `kind` OVER whatever the agent is on.
     *
     * It used to navigate to the owning quoter first, and that navigate existed
     * only to evaluate the lazy module — so "who sent something back?" cost the
     * agent their place and dropped them into a wizard on a quote that may not
     * be the one the form belongs to. The only thing in the panel that ever
     * needed the tool mounted is Apply, and ClientIntakeLink.apply now brings
     * its own intake on screen.
     *
     * The failure toast matters more than it used to: before, a failed load at
     * least left the agent standing on the tool. Now nothing would happen at
     * all. Same sentence as App._openClientIntakeComposer, so both doors fail
     * identically.
     */
    async function openSubmissions(kind) {
        const k = kind === 'commercial' ? 'commercial' : 'personal';
        const L = await ensureLinkModule();
        if (!L || typeof L.openSubmissions !== 'function') {
            const A = window.App;
            if (A && typeof A.toast === 'function') A.toast('Client forms are not available right now.', { type: 'error' });
            return false;
        }
        try { await L.openSubmissions(k); } catch (_) { return false; }
        return true;
    }

    /** Apply one submitted form from anywhere (the Client Portal card). */
    async function applyForm(id, kind) {
        const L = await ensureLinkModule();
        if (!L || typeof L.apply !== 'function') {
            const A = window.App;
            if (A && typeof A.toast === 'function') A.toast('Client forms are not available right now.', { type: 'error' });
            return false;
        }
        try { return await L.apply(id, kind === 'commercial' ? 'commercial' : 'personal'); } catch (_) { return false; }
    }

    /**
     * EAGER palette registration, unlike the two SEND commands, which
     * js/client-intake-link.js registers from its own init() via renderRow() —
     * so they do not exist in the palette until the agent has already opened the
     * tool the command would have taken them to. A command that only appears
     * once you no longer need it is not a command.
     *
     * Two entries, not one that infers its kind: a single command would have to
     * guess from whichever intake happened to have something waiting, and on a
     * cold palette, before the first fetch, it would always mean personal.
     */
    function registerPalette() {
        const P = window.CommandPalette;
        if (!P || typeof P.register !== 'function') return;
        P.register({
            id: 'intake:submissions',
            tool: 'quoting',
            label: 'Client form submissions',
            hint: 'Personal Intake · read, apply or dismiss what clients have sent back',
            icon: '📨',
            run: () => { void openSubmissions('personal'); },
        });
        P.register({
            id: 'intake:submissions-commercial',
            tool: 'commercial',
            label: 'Client form submissions (commercial)',
            hint: 'Commercial Intake · read, apply or dismiss what clients have sent back',
            icon: '📨',
            run: () => { void openSubmissions('commercial'); },
        });
    }

    /**
     * One delegated listener on the card, bound once. The card's innerHTML is
     * rewritten wholesale on every render, so a per-button listener would be
     * re-bound on each poll and lost in between.
     */
    function wire() {
        if (_wired || typeof document === 'undefined') return;
        const card = document.getElementById(CARD_ID);
        if (!card) return;
        _wired = true;
        card.addEventListener('click', (e) => {
            const el = e.target && e.target.closest ? e.target.closest('[data-cif-act], [data-cif-kind], [data-cif-view], [data-cif-reuse]') : null;
            if (!el) return;
            e.preventDefault();
            if (el.dataset.cifReuse) {
                const V = window.ClientIntakeView;
                if (V && typeof V.open === 'function') {
                    V.open(el.dataset.cifReuse, {
                        onApply: (linkId, kind) => { void applyForm(linkId, kind || el.dataset.cifReuseKind); },
                    });
                    return;
                }
                openSubmissions(el.dataset.cifReuseKind);
                return;
            }
            if (el.dataset.cifView) {
                const V = window.ClientIntakeView;
                if (V && typeof V.open === 'function') { V.open(el.dataset.cifView); return; }
            }
            // A row names its own kind; the header and "+N more" open whichever
            // side actually has something waiting, falling back to personal.
            const kind = el.dataset.cifKind
                || (_rows.length ? _rows[0].link.kind : '')
                || 'personal';
            openSubmissions(kind);
        });
    }

    // ── scheduling ─────────────────────────────────────────────────────

    function dashboardVisible() {
        if (typeof document === 'undefined') return false;
        const dv = document.getElementById(DASHBOARD_ID);
        if (!dv) return false;
        if (dv.style && dv.style.display === 'none') return false;
        return !(typeof document.hidden === 'boolean' && document.hidden);
    }

    function tick() {
        wire();
        render();
        if (dashboardVisible()) load(false);
    }

    /**
     * The cache is stale — but the stand-down STANDS. This is what a TTL
     * elapsing means, and it is the difference between the two: an automatic
     * retry must still be able to give up after MAX_CONSECUTIVE_FAILURES, or
     * a dead endpoint gets polled forever.
     */
    function invalidate() {
        _ts = 0;
    }

    /**
     * The agent asked (or the send flow did, before navigating to the
     * dashboard): drop the cache AND the stand-down, and fetch now. An
     * explicit ask always retries — a fix should take effect without a reload.
     */
    function refresh() {
        _ts = 0;
        _failures = 0;
        return load(true);
    }

    /**
     * The hook js/dashboard-widgets.js cannot give us. goHome() and
     * showDashboard() both clear `#dashboardView`'s inline display, so watching
     * that attribute catches every route back to the dashboard.
     */
    function watchDashboard() {
        if (_observer || typeof MutationObserver !== 'function') return;
        const dv = document.getElementById(DASHBOARD_ID);
        if (!dv) return;
        _observer = new MutationObserver(() => { if (dashboardVisible()) tick(); });
        _observer.observe(dv, { attributes: true, attributeFilter: ['style'] });
    }

    function start() {
        if (_timer || typeof window === 'undefined' || typeof window.setInterval !== 'function') return;
        watchDashboard();
        registerPalette();
        _timer = window.setInterval(tick, TTL_MS);
        _kick = window.setTimeout(tick, FIRST_KICK_MS);
        // Paint AND wire now. The first tick is FIRST_KICK_MS away, and a card
        // that looks clickable for two and a half seconds before it is one
        // teaches the agent it does not work.
        render();
        wire();
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') tick();
            });
        }
        window.addEventListener('online', () => { _failures = 0; tick(); });
    }

    function stop() {
        if (_timer) { window.clearInterval(_timer); _timer = null; }
        if (_kick) { window.clearTimeout(_kick); _kick = null; }
        if (_observer) { _observer.disconnect(); _observer = null; }
    }

    if (typeof document !== 'undefined' && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else if (typeof document !== 'undefined') {
        start();
    }

    return {
        summarize, severity, render, appliedText, invalidate, refresh, openSubmissions, applyForm, registerPalette, start, stop, tick, wire,
        getCounts: () => Object.assign({}, _counts),
        getRows: () => _rows.slice(),
        getState: () => _state,
        _setForTest: (rows, counts, state) => { _rows = rows; _counts = counts; _state = state; },
        CARD_ID, TTL_MS, MAX_CONSECUTIVE_FAILURES, MAX_ROWS, OPEN_STATUSES,
    };
})();
